/**
 * The default avatar: a colourful 3D glass orb generated from the person's id.
 *
 * Nothing is stored and nothing is fetched — the same seed always produces the
 * same face, so someone is recognisable across sessions and devices without an
 * upload. It is drawn twice, from one palette: as an SVG in the DOM, and as
 * layered circles on the PixiJS canvas, so a person looks the same on the map
 * as they do in a list.
 *
 * The dimensionality comes from the same three ingredients as the rest of the
 * interface: a lit top-left, a saturated core, and a darker lower rim.
 */

export interface AvatarPalette {
  /** Lit face, toward the light source at the top-left. */
  light: string;
  /** The body colour. */
  base: string;
  /** Shadowed lower-right. */
  dark: string;
  /** Same three as 24-bit numbers, for PixiJS. */
  lightHex: number;
  baseHex: number;
  darkHex: number;
}

function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

/** HSL to #rrggbb — avoids depending on the browser to resolve colours. */
function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const saturation = Math.min(Math.max(s, 0), 100) / 100;
  const lightness = Math.min(Math.max(l, 0), 100) / 100;

  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const second = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = lightness - chroma / 2;

  let [r, g, b] = [0, 0, 0];
  if (hue < 60) [r, g, b] = [chroma, second, 0];
  else if (hue < 120) [r, g, b] = [second, chroma, 0];
  else if (hue < 180) [r, g, b] = [0, chroma, second];
  else if (hue < 240) [r, g, b] = [0, second, chroma];
  else if (hue < 300) [r, g, b] = [second, 0, chroma];
  else [r, g, b] = [chroma, 0, second];

  const channel = (value: number) =>
    Math.round((value + match) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/**
 * Hues are picked from a curated ring rather than the full wheel: the muddy
 * yellow-greens and the near-greys read badly against the page's colour wash,
 * and two people should never land on almost-the-same colour.
 */
const HUES = [222, 250, 276, 300, 330, 350, 12, 28, 160, 174, 190, 205];

export function avatarPalette(seed: string): AvatarPalette {
  const hash = hashSeed(seed);
  const hue = HUES[hash % HUES.length] ?? 222;
  // A small per-person drift keeps two people on the same hue distinguishable.
  const drift = ((hash >> 5) % 14) - 7;

  const light = hslToHex(hue + drift - 16, 95, 72);
  const base = hslToHex(hue + drift, 82, 56);
  const dark = hslToHex(hue + drift + 14, 72, 38);

  return {
    light,
    base,
    dark,
    lightHex: Number.parseInt(light.slice(1), 16),
    baseHex: Number.parseInt(base.slice(1), 16),
    darkHex: Number.parseInt(dark.slice(1), 16),
  };
}

/**
 * The personal companion's colours. Built from the same three-tone recipe as
 * an avatar, but from a hue the person picked rather than a hash, so the
 * character on the map and the one in the corner always match.
 */
export interface CompanionPalette extends AvatarPalette {
  /** Pale tint for the tummy and the glow around the antenna. */
  belly: string;
  bellyHex: number;
}

export function companionPalette(hue: number): CompanionPalette {
  const light = hslToHex(hue - 12, 96, 76);
  const base = hslToHex(hue, 86, 62);
  const dark = hslToHex(hue + 12, 70, 42);
  const belly = hslToHex(hue - 6, 100, 86);
  const hex = (value: string) => Number.parseInt(value.slice(1), 16);
  return {
    light,
    base,
    dark,
    belly,
    lightHex: hex(light),
    baseHex: hex(base),
    darkHex: hex(dark),
    bellyHex: hex(belly),
  };
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || '?';
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * A 96×96 SVG orb. Written as a data URI so it drops straight into an `<img>`
 * with no extra request and no runtime dependency.
 */
export function generatedAvatarSvg(seed: string, name: string): string {
  const palette = avatarPalette(seed);
  const initials = escapeXml(initialsFor(name));
  const id = `a${hashSeed(seed).toString(36).slice(0, 6)}`;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" role="img" aria-label="${initials}">
  <defs>
    <radialGradient id="${id}b" cx="34%" cy="26%" r="86%">
      <stop offset="0%" stop-color="${palette.light}"/>
      <stop offset="52%" stop-color="${palette.base}"/>
      <stop offset="100%" stop-color="${palette.dark}"/>
    </radialGradient>
    <radialGradient id="${id}s" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.95"/>
      <stop offset="60%" stop-color="#ffffff" stop-opacity="0.25"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="${id}r" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.5"/>
      <stop offset="45%" stop-color="#ffffff" stop-opacity="0"/>
    </linearGradient>
  </defs>
  <circle cx="48" cy="48" r="47" fill="url(#${id}b)"/>
  <ellipse cx="34" cy="27" rx="22" ry="16" fill="url(#${id}s)"/>
  <circle cx="48" cy="48" r="46" fill="url(#${id}r)"/>
  <circle cx="48" cy="48" r="46.5" fill="none" stroke="#ffffff" stroke-opacity="0.55" stroke-width="1.5"/>
  <text x="48" y="49" text-anchor="middle" dominant-baseline="central"
        font-family="ui-sans-serif, -apple-system, system-ui, sans-serif"
        font-size="34" font-weight="700" fill="#ffffff" fill-opacity="0.96"
        style="paint-order:stroke">${initials}</text>
</svg>`;
}

export function generatedAvatarDataUri(seed: string, name: string): string {
  // encodeURIComponent rather than base64: it keeps the markup readable in
  // devtools and avoids a btoa round-trip over non-ASCII names.
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(generatedAvatarSvg(seed, name))}`;
}
